import { useMemo, useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { TFunction } from 'i18next';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import { useCancelVisitsMutation } from '../../../../services/visitasApi';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { RootState } from '../../../../redux/store';
import { getDateTime } from '../../../../utils/common';

type CancelVisitsDrawerProps = {
    selectedVisitsIds: number[];
    selectedVisitsSummary?: { id: number; label: string }[];
    setOpen: (value: boolean) => void;
    translate: TFunction;
    clearVisitsSelection?: () => void;
};

export const CancelVisitsDrawer = ({
    selectedVisitsIds,
    selectedVisitsSummary = [],
    setOpen,
    translate,
    clearVisitsSelection
}: CancelVisitsDrawerProps) => {
    const dispatch = useAppDispatch();
    const [motivo, setMotivo] = useState('');
    const [cancelVisits, { isLoading }] = useCancelVisitsMutation();
    const user = useAppSelector((state: RootState) => state.auth.user);

    const visitIdsToCancel = useMemo(
        () => Array.from(new Set(selectedVisitsIds)),
        [selectedVisitsIds]
    );
    const selectedVisitsToDisplay = useMemo(() => {
        const summaryById = new Map(selectedVisitsSummary.map((visit) => [visit.id, visit.label]));

        return visitIdsToCancel.map((id) => ({
            id,
            label: summaryById.get(id) || `#${id}`
        }));
    }, [selectedVisitsSummary, visitIdsToCancel]);
    const selectedCount = visitIdsToCancel.length;

    const closeDrawer = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        const trimmedMotive = motivo.trim();

        if (!visitIdsToCancel.length) {
            return;
        }

        if (!trimmedMotive) {
            enqueueSnackbar(translate('drawerCancelVisits.snackbar.missingObservation'), {
                variant: 'warning'
            });
            return;
        }

        try {
            const fechaHora = getDateTime(new Date().toISOString());
            const nombreUsuario = user?.nombre || '';
            const cuilUsuario = user?.cuil || '';
            const observacionFinal = `${fechaHora} - Cancelado por ${nombreUsuario} (CUIL: ${cuilUsuario}) - Motivo: ${trimmedMotive}`;

            await cancelVisits({
                idsVisita: visitIdsToCancel,
                observacion: observacionFinal
            }).unwrap();

            enqueueSnackbar(
                selectedCount > 1
                    ? translate('drawerCancelVisits.snackbar.successMultiple', {
                          count: selectedCount
                      })
                    : translate('drawerCancelVisits.snackbar.successSingle'),
                { variant: 'success' }
            );

            clearVisitsSelection?.();
            closeDrawer();
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudieron cancelar las visitas',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: '100%'
            }}
        >
            <DrawerHeader onIconClick={closeDrawer} text={translate('drawerCancelVisits.title')} />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#F6F8FB',
                    border: '1px solid #DDE4EA'
                }}
            >
                <Typography fontWeight={700}>
                    {translate('drawerCancelVisits.selectedCount', {
                        count: selectedCount
                    })}
                </Typography>
                <Box
                    sx={{
                        height: 144,
                        overflowY: 'auto',
                        pr: 1
                    }}
                >
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {selectedVisitsToDisplay.map((visit) => (
                            <Chip key={visit.id} label={visit.label} size="small" />
                        ))}
                    </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {translate('drawerCancelVisits.sameObservationInfo', {
                        count: selectedCount
                    })}
                </Typography>
            </Box>

            <TextField
                label={translate('drawerCancelVisits.labelObservation')}
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={motivo}
                inputProps={{ maxLength: 500 }}
                onChange={(event) => setMotivo(event.target.value)}
                helperText={`${motivo.length}/500`}
            />

            <Box
                sx={{
                    mt: 'auto',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    pt: 2
                }}
            >
                <Button variant="outlined" onClick={closeDrawer}>
                    {translate('drawerCancelVisits.buttonCancel')}
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={isLoading}
                >
                    {translate('drawerCancelVisits.buttonConfirm')}
                </Button>
            </Box>
        </Box>
    );
};
